import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Paper, IconButton, Drawer, List, ListItem, ListItemText, Divider, Button, Stack, Snackbar, Alert, Backdrop, CircularProgress } from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import useWebcamMonitor from '../hooks/useWebcamMonitor';
import SystemCheckDialog from './SystemCheckDialog';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiFetch } from '../api';
import websocketService from '../services/websocketService';
import ViolationPanel from './ViolationPanel';
import * as faceapi from 'face-api.js';
import rekognitionService from '../services/rekognitionService';
import { useSecureExam } from '../security';

const INACTIVE_FACE_TIMEOUT = 3 * 60 * 1000; // 3 minutes
const WARNING_POPUP_DURATION = 10000; // 10 seconds
const RANDOM_SNAPSHOT_MIN = 2 * 60 * 1000; // 2 minutes
const RANDOM_SNAPSHOT_MAX = 5 * 60 * 1000; // 5 minutes

const DemoAssessment = () => {
  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState(null);
  const [violations, setViolations] = useState([]);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [transcription, setTranscription] = useState('');
  const [timeLeft, setTimeLeft] = useState(3600); // 1 hour in seconds
  const [isAssessmentStarted, setIsAssessmentStarted] = useState(false);
  const [lastFaceCheck, setLastFaceCheck] = useState(null);
  const [lastNoiseCheck, setLastNoiseCheck] = useState(null);
  const [lastLightingCheck, setLastLightingCheck] = useState(null);
  const [lastTabCheck, setLastTabCheck] = useState(null);
  const [lastWindowCheck, setLastWindowCheck] = useState(null);
  const [lastScreenShareCheck, setLastScreenShareCheck] = useState(null);
  const [lastHeadPoseCheck, setLastHeadPoseCheck] = useState(null);
  const [lastEyeGazeCheck, setLastEyeGazeCheck] = useState(null);
  const [lastDeviceCheck, setLastDeviceCheck] = useState(null);
  const [lastKeyboardCheck, setLastKeyboardCheck] = useState(null);
  const [lastMouseCheck, setLastMouseCheck] = useState(null);
  const [lastBrowserCheck, setLastBrowserCheck] = useState(null);
  const [lastNetworkCheck, setLastNetworkCheck] = useState(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const transcriptionFileRef = useRef(null);
  const checkIntervalRef = useRef(null);
  const [systemCheckOpen, setSystemCheckOpen] = useState(true);
  const [systemCheckPassed, setSystemCheckPassed] = useState(false);
  const audioAnalyser = useRef(null);
  const [warning, setWarning] = useState(null);
  const [warningOpen, setWarningOpen] = useState(false);
  const [inactiveOverlay, setInactiveOverlay] = useState(false);
  const [inactiveStart, setInactiveStart] = useState(null);
  const [isMonitoring, setIsMonitoring] = useState(true);
  const [lastFaceDetected, setLastFaceDetected] = useState(Date.now());
  const [randomSnapshotTimeout, setRandomSnapshotTimeout] = useState(null);
  const [videoReadyCallback, setVideoReadyCallback] = useState(null);
  const [audioReadyCallback, setAudioReadyCallback] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const [faceCheckError, setFaceCheckError] = useState(null);
  const [liveFeedState, setLiveFeedState] = useState({ requested: false, videoReady: false });
  const [videoReallyReady, setVideoReallyReady] = useState(false);
  const liveFeedStateRef = useRef(liveFeedState);
  const userIdRef = useRef(null);
  const examIdRef = useRef(null);
  const examRoomIdRef = useRef(null);
  const lastAwsFaceCheckRef = useRef(null);
  const secureExam = useSecureExam();
  const [examSubmitted, setExamSubmitted] = useState(false);

  const { videoRef, isStreaming, error: webcamError, startStream, stopStream } = useWebcamMonitor({
    video: { width: 640, height: 480, facingMode: 'user' }
  });

  // Utility: load face-api.js models from /models if not already loaded
  const loadFaceApiModels = async () => {
    if (!faceapi.nets.tinyFaceDetector.params) {
      await faceapi.nets.tinyFaceDetector.load('/models');
    }
  };

  // Use local face-api.js for face detection (official example, no backend call)
  const checkMultipleFaces = async () => {
    if (!videoRef.current || videoRef.current.videoWidth === 0 || videoRef.current.videoHeight === 0) {
      // [Student] checkMultipleFaces: video not ready
      return { singleFace: false, faceCount: 0 };
    }
    // Official face-api.js example: load models from /models
    await faceapi.nets.tinyFaceDetector.loadFromUri('/models');
    // Detect faces
    const detections = await faceapi.detectAllFaces(
      videoRef.current,
      new faceapi.TinyFaceDetectorOptions()
    );
    const faceCount = detections.length;
    // [Student] face-api.js detected faces: faceCount, detections
    return { singleFace: faceCount === 1, faceCount };
  };

  // Add a single state to track the current step
  const [step, setStep] = useState('checking_registration'); // 'checking_registration', 'register', 'system_check', 'assessment'

  // Parse query string for userId and examId. Socket room = examId (must match admin's library_id for real-time).
  const query = new URLSearchParams(location.search);
  const userId = query.get('userId');
  const examId = query.get('examId');
  const iacIdParam = query.get('iacId');
  const iacId = iacIdParam != null && iacIdParam !== '' && Number.isFinite(Number(iacIdParam)) ? Number(iacIdParam) : null;
  const examRoomId = (examId && String(examId)) || '';
  const advanced = query.get('advanced') === 'true';

  // Registration and asset check effect
  useEffect(() => {
    if (!userId || !examId) {
      navigate(`/register?userId=${userId || ''}&examId=${examId || ''}`);
      return;
    }
    // Check registration (user_registrations table)
    (async () => {
      setStep('checking_registration');
      const isRegistered = await checkRegistration(userId, examId);
      if (!isRegistered) {
        setStep('register');
        navigate(`/register?userId=${userId}&examId=${examId}`);
        return;
      }
      setStep('system_check');
    })();
    // eslint-disable-next-line
  }, [userId, examId, navigate]);

  // System check dialog proceed handler (now does face verification)
  const handleSystemCheckProceed = async () => {
    setFaceCheckError(null);
    const match = await compareFaceWithSaved(userId);
    if (!match) {
      setFaceCheckError('Face verification failed. Please ensure you are the registered user.');
      return;
    }
    setStep('assessment');
    if (typeof secureExam.markEnvironmentCheckComplete === 'function') {
      secureExam.markEnvironmentCheckComplete();
    }
  };

  // Only run assessment/violation logic after system check passes
  useEffect(() => {
    if (step !== 'assessment') return;
    if (checkIntervalRef.current) {
      clearInterval(checkIntervalRef.current);
    }
    checkIntervalRef.current = setInterval(async () => {
      const now = Date.now();
      // Always check for multiple faces every 5 seconds
      const { singleFace, faceCount } = await checkMultipleFaces();
      // [Student] checkMultipleFaces result: { singleFace, faceCount }
      if (!singleFace) {
        if (faceCount > 1) {
          // [Student] addViolation: Multiple Faces Detected, faceCount
          addViolation('Multiple Faces Detected', `${faceCount} faces detected in frame`, 'high');
        } else {
          // [Student] No Face Detected: do not record as violation, just pause monitoring
          // Optionally show overlay or warning here
        }
      }
      // Only check AWS Rekognition every 3 minutes
      if (!lastAwsFaceCheckRef.current || now - lastAwsFaceCheckRef.current > 180000) {
        lastAwsFaceCheckRef.current = now;
        const awsMatch = await checkAwsFaceRecognitionWithSnapshot();
        if (!awsMatch) {
          // Violation and snapshot handled in checkAwsFaceRecognitionWithSnapshot
        }
      }
      // Lighting check every 30 seconds
      if (!lastLightingCheck || now - lastLightingCheck > 30000) {
        const lightingCheck = checkLighting();
        setLastLightingCheck(now);
        if (!lightingCheck) {
          addViolation('Low Lighting Detected', 'Environment is too dark', 'medium');
        }
      }
      // Tab focus check every 5 seconds
      // DISABLED: Tab switch is no longer a violation
      // if (!lastTabCheck || now - lastTabCheck > 5000) {
      //   const tabCheck = checkTabFocus();
      //   setLastTabCheck(now);
      //   if (tabCheck) {
      //     addViolation('Tab Switched', 'Assessment tab was not in focus', 'high');
      //   }
      // }
      // Screen sharing check every 15 seconds
      if (!lastScreenShareCheck || now - lastScreenShareCheck > 15000) {
        const screenShareCheck = await checkScreenSharing();
        setLastScreenShareCheck(now);
        if (screenShareCheck) {
          addViolation('Screen Sharing Detected', 'Screen sharing or window capture detected', 'high');
        }
      }
      // Head pose check every 20 seconds
      if (!lastHeadPoseCheck || now - lastHeadPoseCheck > 20000) {
        const headPoseCheck = await checkHeadPose();
        setLastHeadPoseCheck(now);
        if (!headPoseCheck) {
          addViolation('Improper Head Position', 'Head is significantly tilted or turned away', 'medium');
        }
      }
      // Browser check every 60 seconds
      if (!lastBrowserCheck || now - lastBrowserCheck > 60000) {
        const browserCheck = checkBrowser();
        setLastBrowserCheck(now);
        if (!browserCheck) {
          addViolation('Unsupported Browser', 'Using an unsupported browser', 'high');
        }
      }
      // Network check every 30 seconds
      if (!lastNetworkCheck || now - lastNetworkCheck > 30000) {
        const networkCheck = checkNetwork();
        setLastNetworkCheck(now);
        if (!networkCheck) {
          addViolation('Network Connection Lost', 'Internet connection lost', 'high');
        }
      }
      // Noise check every 10 seconds (alert only, not a violation)
      if (!lastNoiseCheck || now - lastNoiseCheck > 10000) {
        const noiseCheck = checkAudioLevel();
        setLastNoiseCheck(now);
        if (noiseCheck) {
          showWarning('Noisy background detected');
        }
      }
    }, 5000);
    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [step]);

  // Speech recognition setup with diagnostic logs, only active during assessment
  useEffect(() => {
    if (!isAssessmentStarted) return;
    console.log('[SpeechRecog] useEffect running');
    let recognition;
    let stopped = false;
    let recognitionActive = false;
    if ('webkitSpeechRecognition' in window) {
      console.log('[SpeechRecog] webkitSpeechRecognition is available');
      recognition = new window.webkitSpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US';

      recognition.onresult = (event) => {
        const transcript = Array.from(event.results)
          .map(result => result[0].transcript)
          .join('');
        console.log('[SpeechRecog] onresult:', transcript);
        setTranscription(transcript);
      };

      recognition.onerror = (event) => {
        console.log('[SpeechRecog] onerror:', event);
        recognitionActive = false;
        if (!stopped && isAssessmentStarted && event.error !== 'not-allowed' && event.error !== 'service-not-allowed') {
          setTimeout(() => {
            if (!recognitionActive) recognition.start();
          }, 500);
        }
      };

      recognition.onstart = () => {
        recognitionActive = true;
        console.log('[SpeechRecog] recognition started');
      };
      recognition.onend = () => {
        recognitionActive = false;
        console.log('[SpeechRecog] recognition ended, restarting...');
        setTimeout(() => {
          if (!stopped && isAssessmentStarted && !recognitionActive) recognition.start();
        }, 500);
      };
      recognition.onaudiostart = () => console.log('[SpeechRecog] onaudiostart');
      recognition.onsoundstart = () => console.log('[SpeechRecog] onsoundstart');
      recognition.onspeechstart = () => console.log('[SpeechRecog] onspeechstart');
      recognition.onaudioend = () => console.log('[SpeechRecog] onaudioend');
      recognition.onsoundend = () => console.log('[SpeechRecog] onsoundend');
      recognition.onspeechend = () => console.log('[SpeechRecog] onspeechend');

      recognition.start();

      return () => {
        stopped = true;
        recognition.stop();
      };
    }
  }, [isAssessmentStarted]);

  const togglePanel = () => {
    setIsPanelOpen(!isPanelOpen);
  };

  const startAssessment = () => {
    if (secureExam.enabled && !secureExam.canInteractWithExam) return;
    setIsAssessmentStarted(true);
  };

  useEffect(() => {
    if (secureExam.enabled && secureExam.canInteractWithExam && step === 'assessment') {
      setIsAssessmentStarted(true);
    }
  }, [secureExam.enabled, secureExam.canInteractWithExam, step]);

  useEffect(() => {
    if (!isAssessmentStarted || examSubmitted) return undefined;
    if (secureExam.enabled && secureExam.pauseTimerOnSecurityInterruption && !secureExam.canInteractWithExam) {
      return undefined;
    }
    if (timeLeft <= 0) return undefined;
    const timer = setInterval(() => setTimeLeft((prev) => (prev > 0 ? prev - 1 : 0)), 1000);
    return () => clearInterval(timer);
  }, [isAssessmentStarted, examSubmitted, timeLeft, secureExam.enabled, secureExam.pauseTimerOnSecurityInterruption, secureExam.canInteractWithExam]);

  useEffect(() => {
    if (!userId || !examId) return undefined;
    (async () => {
      try {
        const res = await apiFetch(`/api/exams/${encodeURIComponent(examId)}/attempt?candidateId=${encodeURIComponent(userId)}`);
        const json = await res.json().catch(() => ({}));
        if (json.attempt && json.attempt.remainingSeconds != null) {
          setTimeLeft(Number(json.attempt.remainingSeconds));
        }
        if (json.attempt && json.attempt.status === 'SUBMITTED') {
          setExamSubmitted(true);
        }
      } catch (_) {
        /* optional restore */
      }
    })();
  }, [userId, examId]);

  useEffect(() => {
    if (!userId || !examId || !isAssessmentStarted) return undefined;
    const t = setInterval(() => {
      apiFetch(`/api/exams/${encodeURIComponent(examId)}/attempt`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ candidateId: userId, remainingSeconds: timeLeft, answers: { transcription } })
      }).catch(() => {});
    }, 15000);
    return () => clearInterval(t);
  }, [userId, examId, isAssessmentStarted, timeLeft, transcription]);

  const handleEndExam = async () => {
    setExamSubmitted(true);
    if (secureExam.enabled) {
      await secureExam.finalizeAndSubmit();
    }
    if (userId && examId) {
      await apiFetch(`/api/exams/${encodeURIComponent(examId)}/attempt`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          candidateId: userId,
          remainingSeconds: timeLeft,
          answers: { transcription },
          status: 'SUBMITTED'
        })
      }).catch(() => {});
    }
  };

  // Monitoring effect
  useEffect(() => {
    if (!isAssessmentStarted) return;
    let interval = setInterval(async () => {
      // Face detection
      const faceDetected = await checkMultipleFaces();
      if (advanced) {
        // Advanced mode: apply advanced violations (current logic)
        if (faceDetected.singleFace) {
          setLastFaceDetected(Date.now());
          if (inactiveOverlay) {
            // Immediately try face verification
            const userId = localStorage.getItem('registeredUserId');
            const match = await compareFaceWithSaved(userId);
            if (match) {
              setInactiveOverlay(false);
              setIsMonitoring(true);
              setInactiveStart(null);
              setFaceCheckError(null);
            } else {
              setFaceCheckError('Face verification failed. Please ensure you are the registered user.');
              setInactiveOverlay(true);
              setIsMonitoring(false);
            }
          }
        } else {
          // Immediately pause if no face detected
          if (!inactiveOverlay) {
            setInactiveOverlay(true);
            setIsMonitoring(false);
            setInactiveStart(Date.now());
          }
        }
      } else {
        // Basic mode: only check for face count, apply basic violations
        if (faceDetected.faceCount > 1) {
          addViolation('Multiple Faces Detected', `${faceDetected.faceCount} faces detected in frame`, 'high');
        }
        if (faceDetected.singleFace) {
          setLastFaceDetected(Date.now());
          if (inactiveOverlay) {
            setInactiveOverlay(false);
            setIsMonitoring(true);
            setInactiveStart(null);
            setFaceCheckError(null);
          }
        } else if (faceDetected.faceCount === 0) {
          if (!inactiveOverlay) {
            setInactiveOverlay(true);
            setIsMonitoring(false);
            setInactiveStart(Date.now());
          }
        }
      }
      // Warnings
      if (!checkLighting()) {
        showWarning('Low lighting detected');
        recordViolation('Low Lighting', 'Lighting is too low');
      }
      if (checkNoise()) {
        showWarning('Noisy background detected');
        // No violation for noise
      }
      if (await checkScreenSharing()) {
        showWarning('Screen sharing detected');
        recordViolation('Screen Sharing', 'Screen sharing or window capture detected');
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [isAssessmentStarted, inactiveOverlay, advanced]);

  // Random snapshot effect
  useEffect(() => {
    if (!isAssessmentStarted || !isMonitoring) return;
    let timeout;
    const takeRandomSnapshot = async () => {
      const faceDetected = await checkMultipleFaces();
      if (!faceDetected.singleFace) {
        showWarning('Face verification failed on random snapshot');
        recordViolation('Random Snapshot Face Mismatch', 'Face not verified in random snapshot');
      }
      // Schedule next snapshot
      const next = Math.random() * (RANDOM_SNAPSHOT_MAX - RANDOM_SNAPSHOT_MIN) + RANDOM_SNAPSHOT_MIN;
      timeout = setTimeout(takeRandomSnapshot, next);
      setRandomSnapshotTimeout(timeout);
    };
    // Start first snapshot
    const first = Math.random() * (RANDOM_SNAPSHOT_MAX - RANDOM_SNAPSHOT_MIN) + RANDOM_SNAPSHOT_MIN;
    timeout = setTimeout(takeRandomSnapshot, first);
    setRandomSnapshotTimeout(timeout);
    return () => clearTimeout(timeout);
  }, [isAssessmentStarted, isMonitoring]);

  // Dismiss warning after duration
  useEffect(() => {
    if (warningOpen) {
      const timer = setTimeout(() => setWarningOpen(false), WARNING_POPUP_DURATION);
      return () => clearTimeout(timer);
    }
  }, [warningOpen]);

  // Helper: show warning
  const showWarning = (message) => {
    setWarning(message);
    setWarningOpen(true);
  };

  // Helper: record violation (use examRoomId so server broadcasts to admin's room)
  const recordViolation = (type, details, severity = 'medium', snapshot_url = null) => {
    const queryUserId = userId || localStorage.getItem('registeredUserId');
    const violation = {
      id: Date.now(),
      type,
      timestamp: new Date().toISOString(),
      details,
      userId: queryUserId,
      examId: examRoomId || examId,
      severity,
      snapshot_url,
      ...(iacId != null ? { iacId } : {})
    };
    setViolations(prev => [...prev, violation]);
    // Send to backend in real time
    apiFetch('/api/record-violation', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(violation),
    }).catch(err => {
      // Optionally log or handle error
    });
  };

  // Helper to compare face
  const compareFaceWithSaved = async (userId) => {
    try {
      // Capture current face from webcam
      if (!videoRef.current || videoRef.current.videoWidth === 0 || videoRef.current.videoHeight === 0) {
        return false;
      }
      const canvas = document.createElement('canvas');
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(videoRef.current, 0, 0);
      const dataUrl = canvas.toDataURL('image/jpeg');
      const res = await apiFetch('/api/verify-face', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, image: dataUrl })
      });
      const data = await res.json();
      return !!data.success;
    } catch (err) {
      return false;
    }
  };

  // Format time helper
  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Helper to check registration (user_registrations table via check-registration API)
  const checkRegistration = async (userId, examId) => {
    if (!userId || !examId) return false;
    try {
      const res = await apiFetch('/api/check-registration', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, examId })
      });
      const data = await res.json();
      return !!data.registered;
    } catch (err) {
      return false;
    }
  };

  // Helper: addViolation (calls recordViolation for compatibility)
  const addViolation = (type, details, severity = 'medium', snapshot_url = null) => {
    recordViolation(type, details, severity, snapshot_url);
  };

  // Helper: checkAwsFaceRecognition with snapshot upload
  const checkAwsFaceRecognitionWithSnapshot = async () => {
    try {
      const userId = localStorage.getItem('registeredUserId');
      if (!userId) return false;
      if (!videoRef.current || videoRef.current.videoWidth === 0 || videoRef.current.videoHeight === 0) return false;
      const canvas = document.createElement('canvas');
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(videoRef.current, 0, 0);
      const screenshot = canvas.toDataURL('image/jpeg');
      if (!screenshot) return false;
      const compareRes = await apiFetch('/api/verify-face', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, image: screenshot })
      });
      const compareData = await compareRes.json();
      if (!compareData.success) {
        // Upload snapshot to S3 and include in violation
        let snapshotUrl = null;
        try {
          snapshotUrl = await rekognitionService.uploadSnapshot(userId, examId, screenshot);
        } catch (err) {
          // If upload fails, still record violation without snapshot
          snapshotUrl = null;
        }
        addViolation('Face Verification Failed', 'Face could not be verified', 'high', snapshotUrl);
        return false;
      }
      return true;
    } catch (err) {
      return false;
    }
  };

  // Helper: checkLighting
  const checkLighting = () => {
    if (!videoRef.current || videoRef.current.videoWidth === 0 || videoRef.current.videoHeight === 0) {
      return false;
    }
    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    if (canvas.width === 0 || canvas.height === 0) {
      return false;
    }
    const ctx = canvas.getContext('2d');
    ctx.drawImage(videoRef.current, 0, 0);
    let imageData;
    try {
      imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    } catch (err) {
      return false;
    }
    const data = imageData.data;
    let brightness = 0;
    for (let i = 0; i < data.length; i += 4) {
      brightness += (data[i] + data[i + 1] + data[i + 2]) / 3;
    }
    brightness /= (data.length / 4);
    return brightness > 100; // Threshold for low lighting
  };

  // Helper: checkTabFocus
  const checkTabFocus = () => {
    return document.hidden;
  };

  // Helper: checkScreenSharing
  const checkScreenSharing = async () => {
    try {
      const mediaDevices = await navigator.mediaDevices.enumerateDevices();
      return mediaDevices.some(device => device.label.includes('screen') || device.label.includes('window'));
    } catch (err) {
      return false;
    }
  };

  // Helper: checkHeadPose
  const checkHeadPose = async () => {
    // TODO: Implement local head pose estimation if needed
    // For now, always return true (pass)
    return true;
  };

  // Helper: checkBrowser
  const checkBrowser = () => {
    const userAgent = navigator.userAgent;
    const isChrome = /Chrome/.test(userAgent) && !/Edge/.test(userAgent);
    const isFirefox = /Firefox/.test(userAgent);
    return isChrome || isFirefox;
  };

  // Helper: checkNetwork
  const checkNetwork = () => {
    return navigator.onLine;
  };

  // Helper: checkAudioLevel
  const checkAudioLevel = () => {
    if (!audioAnalyser.current) return false;
    const bufferLength = audioAnalyser.current.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    audioAnalyser.current.getByteFrequencyData(dataArray);
    const average = dataArray.reduce((a, b) => a + b) / bufferLength;
    return average > 30; // Threshold for background noise
  };

  // Helper: checkNoise (alias for checkAudioLevel)
  const checkNoise = () => checkAudioLevel();

  // Listen for start/stop webcam feed events from admin
  useEffect(() => {
    const unsubscribeStart = websocketService.subscribe('start_webcam_feed', (data) => {
      if (data && String(data.userId) === String(userIdRef.current) && String(data.examId) === String(examRoomIdRef.current)) {
        if (!isStreaming) {
          startStream();
        }
        // If video is already ready, set immediately
        if (videoRef.current && videoRef.current.videoWidth > 0 && videoRef.current.videoHeight > 0) {
          setLiveFeedState(prev => {
            const newState = { ...prev, requested: true, videoReady: true };
            liveFeedStateRef.current = newState;
            setTimeout(() => {
            }, 100);
            return newState;
          });
        } else {
          // Wait for video to be ready before setting liveFeedRequested
          setLiveFeedState(prev => {
            const newState = { ...prev, requested: false, videoReady: false };
            liveFeedStateRef.current = newState;
            return newState;
          });
          const checkReady = () => {
            if (videoRef.current && videoRef.current.videoWidth > 0 && videoRef.current.videoHeight > 0) {
              setLiveFeedState(prev => {
                const newState = { ...prev, requested: true, videoReady: true };
                liveFeedStateRef.current = newState;
                return newState;
              });
              clearInterval(waitInterval);
            }
          };
          const waitInterval = setInterval(checkReady, 100);
          setTimeout(() => clearInterval(waitInterval), 5000);
        }
      }
    });
    const unsubscribeStop = websocketService.subscribe('stop_webcam_feed', (data) => {
      if (data && String(data.userId) === String(userIdRef.current) && String(data.examId) === String(examRoomIdRef.current)) {
        setLiveFeedState(prev => {
          const newState = { ...prev, requested: false, videoReady: false };
          liveFeedStateRef.current = newState;
          return newState;
        });
      }
    });
    return () => {
      unsubscribeStart();
      unsubscribeStop();
    };
  }, [userId, examRoomId, isAssessmentStarted, liveFeedState, isStreaming]);

  // Send webcam frames via Socket.IO for admin live view (use examRoomId so server broadcasts to admin's room)
  useEffect(() => {
    let frameInterval;
    if (userId && examRoomId && liveFeedStateRef.current.requested && isStreaming && liveFeedStateRef.current.videoReady) {
      frameInterval = setInterval(() => {
        if (videoRef.current && videoRef.current.videoWidth > 0 && videoRef.current.videoHeight > 0) {
          const canvas = document.createElement('canvas');
          canvas.width = videoRef.current.videoWidth;
          canvas.height = videoRef.current.videoHeight;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(videoRef.current, 0, 0);
          const frame = canvas.toDataURL('image/jpeg');
          websocketService.sendMessage('webcam_frame', {
            examId: examRoomId,
            userId,
            frame,
          });
        }
      }, 1000); // send a frame every second
    }
    return () => {
      if (frameInterval) {
        clearInterval(frameInterval);
      }
    };
  }, [userId, examRoomId, videoRef, isStreaming, liveFeedState.requested, liveFeedState.videoReady]);

  // Log every heartbeat sent, with robust logging (use examRoomId so admin receives in same room)
  useEffect(() => {
    if (!userId || !examRoomId) return undefined;
    const sendBeat = () => {
      websocketService.sendMessage('heartbeat', { examId: examRoomId, userId });
    };
    sendBeat();
    const heartbeatInterval = setInterval(sendBeat, 10000);
    return () => {
      clearInterval(heartbeatInterval);
    };
  }, [userId, examRoomId]);

  // Force webcam to start before system check, but only after videoRef is mounted
  useEffect(() => {
    if (videoRef.current && !isStreaming) {
      startStream();
    }
  }, [videoRef, isStreaming, startStream]);

  // Keep ref in sync with state
  useEffect(() => {
    liveFeedStateRef.current = liveFeedState;
  }, [liveFeedState]);

  // Keep userIdRef, examIdRef, and examRoomIdRef in sync
  useEffect(() => {
    userIdRef.current = userId;
    examIdRef.current = examId;
    examRoomIdRef.current = examRoomId;
  }, [userId, examId, examRoomId]);

  // Periodically send transcript to backend and admin
  useEffect(() => {
    console.log('[Transcript] useEffect running', { isAssessmentStarted, userId, examId, transcription });
    if (!isAssessmentStarted || !userId || !examId) return;
    const interval = setInterval(() => {
      if (transcription && transcription.length > 0) {
        console.log('[Transcript] Sending transcript to backend:', { userId, examId: examRoomId || examId, transcript: transcription });
        apiFetch('/api/record-transcript', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId,
            examId: examRoomId || examId,
            transcript: transcription,
            ...(iacId != null ? { iacId } : {})
          })
        }).catch(err => {
          console.log('[Transcript] Error sending transcript to backend:', err);
        });
        if (websocketService && websocketService.sendMessage) {
          console.log('[Transcript] Sending transcript to admin via websocket:', { userId, examId: examRoomId, transcript: transcription });
          websocketService.sendMessage('transcript', {
            userId,
            examId: examRoomId,
            transcript: transcription,
            ...(iacId != null ? { iacId } : {})
          });
        }
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [isAssessmentStarted, userId, examId, examRoomId, transcription, iacId]);

  // Call audioReadyCallback when the audioAnalyser is ready
  useEffect(() => {
    if (audioAnalyser.current && audioReadyCallback) {
      audioReadyCallback();
    }
  }, [audioAnalyser, audioReadyCallback]);

  // Ensure websocketService connects with same room as admin (examRoomId = library_id when present)
  useEffect(() => {
    if (userId && examRoomId) {
      websocketService.connect(examRoomId, userId, 'user');
    }
  }, [userId, examRoomId]);

  // Robust callback ref for videoRef only
  const setVideoElement = React.useCallback((el) => {
    videoRef.current = el;
  }, [videoRef]);

  // Force MediaPipe FaceDetection to load models from /models
  if (typeof window !== 'undefined') {
    window.FaceDetection = window.FaceDetection || {};
    window.FaceDetection.locateFile = (file) => `/models/${file}`;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'row', minHeight: '100vh' }}>
      <Box sx={{ flex: 1 }}>
        {/* Registration check and loading */}
        {step === 'checking_registration' && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }}>Checking registration...</Typography>
          </Box>
        )}
        {/* Registration step handled by redirect, but fallback UI */}
        {step === 'register' && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6">Redirecting to registration...</Typography>
            {faceCheckError && <Typography color="error">{faceCheckError}</Typography>}
          </Box>
        )}
        {/* Video element for both system check and assessment */}
        {(step === 'system_check' || step === 'assessment') && (
          <Box sx={{ mb: 2, position: 'relative', width: '100%', maxWidth: '640px', margin: '0 auto' }}>
            <video
              ref={el => {
                setVideoElement(el);
              }}
              autoPlay
              playsInline
              onLoadedData={() => {
                if (videoReadyCallback) {
                  videoReadyCallback();
                }
              }}
              style={{ 
                width: '100%',
                height: 'auto',
                border: '3px solid #1976d2',
                borderRadius: '8px'
              }}
            />
            <Box sx={{ 
              position: 'absolute', 
              top: 10, 
              right: 10, 
              backgroundColor: '#1976d2',
              color: 'white',
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: '0.8rem'
            }}>
              {isStreaming ? 'Webcam Active' : 'Webcam Inactive'}
            </Box>
            {webcamError && (
              <Box sx={{ p: 2, color: 'error.main', textAlign: 'center' }}>
                Webcam Error: {webcamError.message || webcamError.toString()}
              </Box>
            )}
          </Box>
        )}
        {/* System check dialog */}
        {step === 'system_check' && (
          <SystemCheckDialog
            open={true}
            onProceed={handleSystemCheckProceed}
            videoRef={videoRef}
            audioAnalyser={audioAnalyser}
            onVideoReady={cb => {
              setVideoReadyCallback(() => cb);
            }}
            onAudioReady={cb => {
              setAudioReadyCallback(() => cb);
            }}
            faceCheckError={faceCheckError}
            bypassAudioReady={true} // custom prop for debugging
          />
        )}
        {/* Assessment UI */}
        {step === 'assessment' && (
        <Box sx={{ p: 3, maxWidth: 800, margin: '0 auto' }}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h5">
                Mock Assessment
              </Typography>
              <Typography variant="h6" sx={{ color: timeLeft < 300 ? 'error.main' : 'text.primary' }}>
                Time Remaining: {formatTime(timeLeft)}
              </Typography>
            </Box>
            {!isAssessmentStarted ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" gutterBottom>
                  Welcome to the Mock Assessment
                </Typography>
                <Typography paragraph>
                  This is a demonstration of the proctoring system. The system will monitor your environment in the background.
                </Typography>
                <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 1 }}>
                  {secureExam.enabled && !secureExam.isFullscreen && (
                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      onClick={() => { secureExam.enterFullscreen().catch(() => {}); }}
                    >
                      Go Fullscreen
                    </Button>
                  )}
                  <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={startAssessment}
                    size="large"
                    disabled={secureExam.enabled && !secureExam.canInteractWithExam}
                  >
                    Start Assessment
                  </Button>
                </Stack>
              </Box>
            ) : (
              <Box>
                {examSubmitted ? (
                  <Typography variant="h6" gutterBottom>Exam submitted. You may close this window.</Typography>
                ) : null}
                <Box sx={{ pointerEvents: (!secureExam.canInteractWithExam || examSubmitted) ? 'none' : 'auto', opacity: examSubmitted ? 0.6 : 1 }}>
                <Typography variant="h6" gutterBottom>
                  Sample Questions
                </Typography>
                <Box sx={{ mb: 4 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    1. What is the capital of France?
                  </Typography>
                  <Typography variant="subtitle1" gutterBottom>
                    2. Solve for x: 2x + 5 = 15
                  </Typography>
                  <Typography variant="subtitle1" gutterBottom>
                    3. Explain the concept of photosynthesis.
                  </Typography>
                </Box>
                <Typography variant="h6" gutterBottom>
                  Audio Transcription
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, mb: 4, minHeight: 100, maxHeight: 200, overflow: 'auto' }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {transcription || 'No audio detected yet...'}
                  </Typography>
                </Paper>
                {!examSubmitted && (
                  <Button variant="contained" color="error" onClick={handleEndExam}>
                    End Exam
                  </Button>
                )}
                </Box>
              </Box>
            )}
            {/* Warning Snackbar */}
            <Snackbar open={warningOpen} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
              <Alert severity="warning" sx={{ width: '100%' }}>{warning}</Alert>
            </Snackbar>
            {/* Inactive Overlay */}
            <Backdrop open={inactiveOverlay} sx={{ zIndex: 2000, color: '#fff', backgroundColor: 'rgba(0,0,0,0.7)' }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ mb: 2 }}>Monitoring Paused</Typography>
                <Typography variant="h6">No face detected. Please return to the camera to resume monitoring.</Typography>
                <CircularProgress sx={{ mt: 3 }} />
              </Box>
            </Backdrop>
          </Paper>
          </Box>
        )}
      </Box>
      {/* Violation Panel on the right, always visible for assessment */}
      <Box sx={{ width: 350, bgcolor: 'background.default', borderLeft: '1px solid #eee', p: 2 }}>
        <ViolationPanel violations={violations} />
      </Box>
    </Box>
  );
};

export default DemoAssessment; 